import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined'
import { client } from '../../hooks/Client'
import useSettings from '../../hooks/Settings'
import {
    useFirestoreAllArrayContains,
    useFirestoreNoCompagnyOrderBy,
    useFirestoreLoaded,
} from '../../firebase/useFirestore'
import { indexProjectMarks, starredProjects, filterProjectsByMarks, projectLabelLookup, visibleLabelKeys } from '../../helpers/portfolio/projectMarks'
import ProjectLabelFilter from '../portfolio/ProjectLabelFilter'
import ProjectLabelChip from '../portfolio/ProjectLabelChip'
import useProjectNotesColumn from '../../hooks/portfolio/useProjectNotesColumn'
import { daysSince, daysColorClass, formatDayAge } from '../../utils/activityAge'
import useRetentionScoresBulk from '../../hooks/retention/useRetentionScoresBulk'
import useAllDataQualityScores from '../../hooks/useAllDataQualityScores'
import { dataQualityBand } from '../../helpers/dashboard/dataQualityDisplay'
import countOpenTasksByProject from '../../helpers/portfolio/countOpenTasksByProject'
import useProjectFollowUpTasks from '../../hooks/portfolio/useProjectFollowUpTasks'
import countOpenRequestsByTenant from '../../helpers/updateRequests/countOpenRequestsByTenant'
import useAllUpdateRequests from '../../hooks/updateRequests/useAllUpdateRequests'
import Table from '../common/table/Table'
import TableCell from '../common/table/TableCell'
import Tooltip from '../common/Tooltip'
import StaticCardLabel from './StaticCardLabel'
import DashboardCardEmptyState from './DashboardCardEmptyState'

// AI-CONTEXT: See docs/patterns.md#dashboard-static-card

const DECCOS_MANAGEMENT_TENANT = '123423HJKHVKH'

// Eén bron voor de titel: de kaart zelf en de naam in 'Aanpassen'
// (STATIC_CARD_LABELS) lezen allebei deze constante.
export const PORTFOLIO_CARD_TITLE = 'Projecten met ster'

const columns = [
    { key: 'name', label: 'NAAM' },
    { key: 'login', label: 'LAATSTE LOGIN', searchable: false },
    { key: 'events', label: 'ACTIVITEITEN', searchable: false },
    { key: 'results', label: 'ONDERZOEKEN', searchable: false },
    { key: 'environment', label: 'OMGEVING', searchable: false },
]

const openTasksColumn = { key: 'openTasks', label: 'OPEN TAKEN', searchable: false }
// Zelfde vorm als OPEN TAKEN ernaast: allebei tellen ze openstaand werk.
// De kaart is volle breedte (staticCardRegistry), dus er is ruimte voor.
const openRequestsColumn = { key: 'openRequests', label: 'OPEN VERZOEKEN', searchable: false }

// Beheertenant-variant: extra kolommen met de bevroren datakwaliteitsscore, de
// afspraken die bij dit project nog openstaan en de updateverzoeken die deze
// organisatie zelf heeft ingediend.
const managementColumns = [
    ...columns.slice(0, 4),
    { key: 'quality', label: 'DATAKWALITEIT', searchable: false },
    openTasksColumn,
    openRequestsColumn,
    columns[4],
]

// Portfoliovariant: geen datakwaliteit, geen afspraken en geen updateverzoeken
// — dat is beheerwerk van Deccos zelf.
// AI-CONTEXT: See docs/features/deccos-projectafspraken.md
// AI-CONTEXT: See docs/features/update-aanvragen.md
const portfolioColumns = columns

// Welke labels een project heeft, direct na de naam: anders filter je op iets
// wat je niet ziet. Alleen lezen; zetten gebeurt in het projectenoverzicht.
const labelsColumn = { key: 'labels', label: 'LABELS', searchable: false }

/**
 * @param {Array<{key: string}>} base - De kolommen van deze omgeving
 * @param {boolean} withLabels - Of deze organisatie labels heeft ingesteld
 * @returns {Array<{key: string}>}
 */
const withLabelsColumn = (base, withLabels) => (withLabels ? [base[0], labelsColumn, ...base.slice(1)] : base)

// De nieuwste projectnotitie (tabblad 'Notities' op de projectpagina). Die
// notities bestaan alleen in de beheeromgeving, dus de kolom ook.
const notesColumn = { key: 'notes', label: 'NOTITIES', searchable: false }

/**
 * @param {Array<{key: string}>} base - De kolommen tot nu toe
 * @param {boolean} withNotes - Of deze omgeving projectnotities heeft (alleen de beheertenant)
 * @returns {Array<{key: string}>} Met NOTITIES direct na LABELS, of na NAAM
 */
const withNotesColumn = (base, withNotes) => {
    if (!withNotes) return base
    const na = base.findIndex((c) => c.key === 'labels') + 1 || 1
    return [...base.slice(0, na), notesColumn, ...base.slice(na)]
}

const renderQualityScore = (entry, loading) => {
    if (!entry) return loading ? '…' : '—'
    const band = dataQualityBand(entry.Score)
    return (
        <span
            className={`data-quality-score--${band}`}
            title={entry.Provisional ? 'Score voorlopig — minder dan 6 maanden data' : undefined}
        >
            {entry.Score}{entry.Provisional ? '*' : ''}
        </span>
    )
}

const renderLastLogin = (item) => {
    const d = daysSince(item.LastLogin)

    return d !== null
        ? <span className={daysColorClass(d)}>{formatDayAge(d)}</span>
        : '—'
}

// Activity / research counts derived from bulk-fetched signals (no per-row listeners).
// Window: 16 weken voor activiteiten, 12 maanden voor onderzoek.
const renderSignalCount = (score, scoresLoading, countKey, lastDateKey) => {
    if (scoresLoading && !score) return '…'
    const count = score?.signals?.[countKey] ?? 0
    const lastDate = score?.signals?.[lastDateKey] ?? null
    const d = lastDate ? daysSince({ toDate: () => lastDate }) : null
    return (
        <span>
            {count}
            {d !== null && <span className={daysColorClass(d)}> ({formatDayAge(d)})</span>}
        </span>
    )
}

const projectUrl = (item) => {
    const host = window.location.hostname
    return host === 'localhost'
        ? `http://${host}:3001/${item.CompagnyID}/home`
        : `https://${host}/${item.CompagnyID}/home`
}

const DashboardPortfolioCard = () => {
    const { portfolio, projectLabels } = useSettings()
    // Het filter 'Label' ('' = alle), met dezelfde labels als het projectenoverzicht.
    const [labelKey, setLabelKey] = useState('')
    const isManagementTenant = client === DECCOS_MANAGEMENT_TENANT

    // Management tenant: read all CompagnyMeta. Portfolio tenant: only own children.
    const allMeta = useFirestoreNoCompagnyOrderBy(
        isManagementTenant ? 'CompagnyMeta' : null,
        'Timestamp',
        'desc'
    )
    const childProjects = useFirestoreAllArrayContains(
        isManagementTenant ? null : 'CompagnyMeta',
        'Parent',
        client
    )
    // Alleen de projecten die déze omgeving een ster gaf in het portfolio-
    // overzicht, ongeacht hun status (keuze Gijs 2026-10-16). De sterren zijn
    // van de kijker: ProjectMarks met deze omgeving als CompagnyID.
    // AI-CONTEXT: See src/helpers/portfolio/projectMarks.js
    const [projectMarks, marksLoaded] = useFirestoreLoaded('ProjectMarks')
    const marksByProject = useMemo(() => indexProjectMarks(projectMarks), [projectMarks])

    const shownProjects = useMemo(() => {
        const source = isManagementTenant
            ? (Array.isArray(allMeta) ? allMeta : [])
            : (Array.isArray(childProjects) ? childProjects : [])

        return starredProjects(source, marksByProject)
            .sort((a, b) => (a.CommunityName || '').localeCompare(b.CommunityName || ''))
    }, [isManagementTenant, allMeta, childProjects, marksByProject])

    const { options: labelOptions, byKey: labelByKey } = useMemo(() => projectLabelLookup(projectLabels), [projectLabels])
    const showLabels = labelOptions.length > 0

    // Kolom NOTITIES met '+ Notitie' — alleen de beheeromgeving heeft
    // projectnotities, dus elders wordt er niets gelezen.
    const { renderNoteCell, noteModal } = useProjectNotesColumn(isManagementTenant)

    /**
     * De labels van één project als chips, of een streepje zonder label.
     * @param {{CompagnyID: string}} item
     * @returns {JSX.Element | string}
     */
    const renderLabelChips = (item) => {
        const keys = visibleLabelKeys(marksByProject.get(item.CompagnyID), labelByKey)
        if (keys.length === 0) return '—'
        return (
            <div className='project-label-chips'>
                {keys.map((key) => <ProjectLabelChip key={key} label={labelByKey[key]} labelKey={key} />)}
            </div>
        )
    }
    // Het filter werkt binnen de projecten met een ster; de signalen hieronder
    // blijven op de hele lijst, zodat een labelwissel niets opnieuw ophaalt.
    const filteredProjects = useMemo(
        () => filterProjectsByMarks(shownProjects, marksByProject, { labelKey }),
        [shownProjects, marksByProject, labelKey]
    )

    const { scoresByEntityId, loading: scoresLoading } = useRetentionScoresBulk(shownProjects)
    const { scoresByCompagny, loading: qualityLoading } = useAllDataQualityScores(isManagementTenant)

    // Afspraken die deze omgeving bij haar projecten vastlegde, daarna per
    // project geteld — geen luisteraar per rij. Gedeeld met de afsprakenkaart.
    const projectTasks = useProjectFollowUpTasks(isManagementTenant)
    const tasksLoaded = Array.isArray(projectTasks)
    const openTasksByProject = useMemo(
        () => countOpenTasksByProject(projectTasks),
        [projectTasks]
    )

    // Updateverzoeken van alle organisaties in een keer, daarna per organisatie
    // geteld — geen luisteraar per rij.
    const updateRequests = useAllUpdateRequests(isManagementTenant)
    const requestsLoaded = Array.isArray(updateRequests)
    const openRequestsByTenant = useMemo(
        () => countOpenRequestsByTenant(updateRequests),
        [updateRequests]
    )

    if (!isManagementTenant && portfolio !== 'block') return null

    // Pas iets beweren als zowel de projecten als de sterren binnen zijn: een
    // lege tabel tijdens het laden leest als 'geen projecten met een ster'.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    const sourceLoaded = (isManagementTenant
        ? Array.isArray(allMeta)
        : Array.isArray(childProjects)) && marksLoaded
    const isEmpty = sourceLoaded && shownProjects.length === 0

    return (
        <div>
            <StaticCardLabel
                title={PORTFOLIO_CARD_TITLE}
                pill={{ variant: 'portfolio', label: 'Portfolio' }}
            />
            {!sourceLoaded ? (
                <p>…</p>
            ) : isEmpty ? (
                <DashboardCardEmptyState type='portfolio-projects' />
            ) : (
            <Table
                columns={withNotesColumn(withLabelsColumn(isManagementTenant ? managementColumns : portfolioColumns, showLabels), isManagementTenant)}
                items={filteredProjects}
                searchBarStart={<ProjectLabelFilter options={labelOptions} value={labelKey} onChange={setLabelKey} />}
                getItemKey={item => item.CompagnyID}
                searchAccessor={(item) => item.CommunityName || ''}
                searchPlaceholder="Zoeken op naam"
                emptyMessage={labelKey ? 'Geen projecten met een ster en dit label.' : 'Geen projecten met een ster gevonden.'}
                renderRow={(item) => {
                    const score = scoresByEntityId.get(item.CompagnyID)
                    return (
                        <>
                            <TableCell>
                                <Link to={`/${client}/portfolioprojectdetails/${item.CompagnyID}`}>
                                    {item.CommunityName || '—'}
                                </Link>
                            </TableCell>
                            {showLabels && (
                                <TableCell>
                                    {/* Een label dat de organisatie verborg of weghaalde blijft
                                        op het project staan, maar hoort hier niet. */}
                                    {renderLabelChips(item)}
                                </TableCell>
                            )}
                            {isManagementTenant && (
                                <TableCell>{renderNoteCell(item)}</TableCell>
                            )}
                            <TableCell>{renderLastLogin(item)}</TableCell>
                            <TableCell>{renderSignalCount(score, scoresLoading, 'eventsCountInWindow', 'lastEventDate')}</TableCell>
                            <TableCell>{renderSignalCount(score, scoresLoading, 'researchesCountInWindow', 'lastResearchDate')}</TableCell>
                            {isManagementTenant && (
                                <TableCell>
                                    {renderQualityScore(scoresByCompagny.get(item.CompagnyID), qualityLoading)}
                                </TableCell>
                            )}
                            {isManagementTenant && (
                                <TableCell>
                                    {/* Pas iets beweren als de afspraken binnen zijn: een streepje
                                        tijdens het laden leest als 'niets openstaan'.
                                        AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading */}
                                    {tasksLoaded ? (openTasksByProject.get(item.CompagnyID) || '—') : '…'}
                                </TableCell>
                            )}
                            {isManagementTenant && (
                                <TableCell>
                                    {/* Zelfde regel als hiernaast: pas iets beweren als de
                                        verzoeken binnen zijn.
                                        AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading */}
                                    {requestsLoaded ? (openRequestsByTenant.get(item.CompagnyID) || '—') : '…'}
                                </TableCell>
                            )}
                            <TableCell>
                                <Tooltip content="Open project-omgeving" top="-35px">
                                    <a href={projectUrl(item)} target="_blank" rel="noreferrer">
                                        <LaunchOutlinedIcon />
                                    </a>
                                </Tooltip>
                            </TableCell>
                        </>
                    )
                }}
            />
            )}
            {noteModal}
        </div>
    )
}

export default DashboardPortfolioCard
